import { useRouter } from 'next/router'
import { ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { RootState, useSelector, useDispatch } from '@/store'
import { setContactCorporation } from '@/store/contactCorporation/slice'
import { ContactCorporation } from '@/store/contactCorporation/types'
import { theme } from '@/styles/theme'

import { corporationLabels } from './CorporationLabel'
import { Button } from '@/components/elements/Button'
import { FormInput } from '@/components/elements/enFormInput'
import { FormLabel } from '@/components/elements/enFormLabel'
import { LinkText } from '@/components/elements/LinkText'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/enlayout'
import { Head } from '@/components/enlayouts/Head'

export const CorporationPage = () => {
  const form = useSelector((state: RootState) => state.contactCorporation)
  const dispatch = useDispatch()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactCorporation>()

  const router = useRouter()

  const onSubmit = (data: ContactCorporation) => {
    dispatch(setContactCorporation(data))
    router.push('/en/contact/corporation/confirm')
  }

  return (
    <>
      <Head
        title="Corporate"
        description="For inquiries about our business and servies or proposals for collaboration"
        ogp={{ type: 'article', url: 'en/contact/corporation/' }}
      />
      <CorporationMain>
        <SectionTitle
          enTitleText="Corporate"
          subTitleText=""
          color="normal"
        />
        <Spacer h={{ pc: 69, sp: 59 }} />

        <Note>
          <ol>
            <li>
              For proposals to each service or subsidiary, please contact the respective contact points directly.
              <br />
              If you are unsure about the target service, such as for new business areas, please indicate that and contact us using the inquiry form below.
            </li>
            <li>
              Depending on the  inquiry, it may take longer for us to respond, or we may be unable to provide a response.
            </li>
            <li>
              If you use domain-specific reception settings, please allow reception from the domains @dena.jp and @dena.com.
            </li>
          </ol>
        </Note>
        <Spacer h={{ pc: 100, sp: 69 }} />

        <Form method="post" noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormBackground>
            <FormMain>
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.company} />
              </FormLabelWrapper>
              <FormInput
                id="company"
                type="text"
                register={register('company')}
                defaultValue={form.company}
              />
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.fullName} />
              </FormLabelWrapper>
              <FormInner>
                <FormGroup>
                  <FormLabel {...corporationLabels.lastName} />
                  <FormInput
                    id="lastName"
                    type="text"
                    register={register('lastName', {
                      required: {
                        value: true,
                        message: 'Please fill the required field.',
                      },
                    })}
                    errorMessage={errors.lastName}
                    defaultValue={form.lastName}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel {...corporationLabels.firstName} />
                  <FormInput
                    id="firstName"
                    type="text"
                    register={register('firstName', {
                      required: {
                        value: true,
                        message: 'Required',
                      },
                    })}
                    errorMessage={errors.firstName}
                    defaultValue={form.firstName}
                  />
                </FormGroup>
              </FormInner>
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.email} />
              </FormLabelWrapper>
              <FormInput
                id="email"
                type="email"
                register={register('email', {
                  required: {
                    value: true,
                    message: 'Please fill the required field.',
                  },
                  pattern: {
                    message: 'Please enter correctly.',
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  },
                })}
                placeholder="e.g. XXX@XXX.ne.jp"
                errorMessage={errors.email}
                defaultValue={form.email}
              />
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.tel} />
              </FormLabelWrapper>
              <FormInput
                id="tel"
                type="tel"
                maxLength={13}
                register={register('tel', {
                  pattern: {
                    message: 'Please enter correctly.',
                    value: /^[-0-9]+$/,
                  },
                })}
                placeholder="e.g. 00-0000-0000"
                errorMessage={errors.tel}
                defaultValue={form.tel}
              />
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.title} />
              </FormLabelWrapper>
                <FormInput
                  id="title"
                  type="text"
                  register={register('title')}
                  defaultValue={form.title}
                  placeholder="e.g. Subject of the contents"
                  annotation="If this is not your first time, please briefly fill in the details of your past inquiries."
                />
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.detail} />
              </FormLabelWrapper>
              <FormComment>
                <FormInput
                  id="detail"
                  type="text"
                  register={register('detail', {
                    required: {
                      value: true,
                      message: 'Please fill the required field.',
                    },
                  })}
                  as="textarea"
                  placeholder="Please enter the details of your inquiry."
                  errorMessage={errors.detail}
                  defaultValue={form.detail}
                />
                <p>*If you are proposing a promotion, please fill in the floowing:</p>
                <ol>
                  <li>Summary of the proposal</li>
                  <li> Details of the proposal</li>
                  <li> Expected effects of the proposal</li>
                </ol>
              </FormComment>
            </FormMain>
            <FormBottom>
              Personal information acquired from this inquiry form is used solely for the purpose of responding to your inquiry, 
              <br />
              The Company properly handles personal informarion according to its 
              <LinkText
                path="/en/legal/privacy-policy/"
                text=" Privacy Policy"
              />
              .
            </FormBottom>
          </FormBackground>
          <ButtonWrapper>
            <SubmitButton btnStyle="outline" type="submit">
              Confirm
            </SubmitButton>
          </ButtonWrapper>
        </Form>
      </CorporationMain>
    </>
  )
}

CorporationPage.getLayout = function getLayout(page: ReactElement) {
  const pageTitle = {
    title: 'Inquiry',
    subTitle: '',
  }

  const breadCrumbs = [
    { name: 'Home', path: '/en/' },
    { name: 'Inquiry', path: '/en/contact/' },
    { name: 'Corporate', path: '' },
  ]

  return (
    <TitleLayout {...pageTitle} breadCrumbs={breadCrumbs} isRequiredBackground>
      {page}
    </TitleLayout>
  )
}

const CorporationMain = styled.div`
  width: 1040px;
  padding-bottom: 130px;
  margin: 69px auto 0;

  @media screen and (max-width: 1047px) {
    width: 100%;
    margin: 40px auto 0;
    padding: 0 20px 70px;
  }
`

const Note = styled.div`
  text-align: left;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0.32px;
  font-weight: 400;

  ol {
    padding-left: 25px;
    list-style-type: disc;
  }

  @media screen and (max-width: 1047px) {
    font-size: 15px;
    line-height: 25px;
    letter-spacing: 0.3px;
  }
`

const Form = styled.form``

const FormMain = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 40px 30px;

  @media screen and (max-width: 1047px) {
    grid-template-columns: 1fr;
    gap: 16px 0;
  }
`

const FormBackground = styled.div`
  background-color: ${theme.colors.backgroundGrey};
  padding: 86px 74px 99px;

  @media screen and (max-width: 1047px) {
    padding: 27px 20px 40px;
  }
`

const FormLabelWrapper = styled.div`
  padding-top: 14px;
`

const FormInner = styled.div`
  display: flex;
  align-items: center;
  column-gap: 43px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    align-items: flex-start;
    row-gap: 14px;
  }
`

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  column-gap: 26px;
  width: 50%;

  div {
    flex: 1;
  }

  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`


const FormComment = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  line-height: 26px;
  color: ${theme.colors.grey};

  ol {
    padding-left: 25px;
    list-style-type: decimal;
  }
`

const FormBottom = styled.p`
  line-height: 26px;
  letter-spacing: 0.32px;
  margin-top: 74px;
  font-weight: 400;

  @media screen and (max-width: 1047px) {
    margin-top: 36px;
    font-size: 15px;
    line-height: 25px;
    letter-spacing: 0.3px;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 107px;

  @media screen and (max-width: 1047px) {
    margin-top: 54px;
  }
`

const SubmitButton = styled(Button)`
  width: 198px;
  font-weight: 400;
  border-width: 1px;

  @media screen and (max-width: 1047px) {
    font-size: 18px;
  }
`
